import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './styles/globals.css'
import './i18n' // Import i18n configuration
import './utils/api-client' // Import API client with language interceptor
import { initAnalytics } from './utils/analytics'
import { initConsent } from './utils/consent'
import { listenForInstallPrompt } from './utils/install-prompt'
import App from './App'
import GymAdminApp from './gym-admin/GymAdminApp'
import { ScrollToTop } from './components/ScrollToTop'

// The same bundle serves both the member app (app.divepeaks.*) and the admin
// panel. Pick the shell from the hostname.
//
// Two hosts, one panel: `gym.` is what gym staff have always typed, and
// `admin.` is where a federation running a trophy expects to go. They render
// the same app — trophies and their stages are places in the same switcher as
// gyms and crags, so splitting them into a second shell would mean two copies
// of the sidebar, the guards and the switcher, and two tabs for anyone who is
// both a gym's admin and a circuit's.
const isGymHost = /^(gym|admin)\./.test(window.location.hostname)

// Chromium offers the install dialog once, early, before any page has mounted;
// the member dashboard's install card opens it later. The panel offers none.
if (!isGymHost) listenForInstallPrompt()

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // No replayIntegration. Session Replay recorded one in ten sessions in
    // full — what people typed, opened and read — which is a different thing
    // from error monitoring and is not defensible as strictly necessary. What
    // is left captures the exception, its stack and the route it happened on:
    // enough to fix a crash, and nothing that needs a banner. Do not add it
    // back without putting it behind the consent gate in utils/consent.ts.
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Correlates frontend traces with backend requests
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.divepeaks/,
    ],
    tracesSampleRate: 1.0,
  });
}

// Analytics subscribes to the consent gate first and loads nothing by itself;
// initConsent then reads back whatever the visitor already answered. Order is
// not critical — the gate replays its current state to late subscribers — but
// registering before asking keeps the flow readable.
initAnalytics()
initConsent()

// Register service worker (member PWA only — the gym-admin app is a desktop tool
// and does not need offline/install support).
if ('serviceWorker' in navigator && !isGymHost) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Service worker registration failed; the app still works without it.
    });
  });
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Router>
      <ScrollToTop />
      {isGymHost ? <GymAdminApp /> : <App />}
    </Router>
  </React.StrictMode>
)
